import { FormikValues } from "formik"
import * as Yup from "yup"
import { AnyObject } from "yup/lib/types"
import { UnreachableCaseError } from "shared/Helpers/lang"
import { createEnumChecker } from "shared/Helpers/enums"
import { Logger } from "shared/Helpers/logging"
import { FormInputField, InputType, ValidationRule, ValidationType } from "./formTypes"
import { IConfigField, InputFieldType } from "./dynamicFormFieldsTypes"
import {
    DeliveryPayloadType,
    IBasketMeetingPlanningFormFieldOptional,
    IEditMeetingOrderData, IFormikSelectOptionsProps
} from "../../Types/MeetingPlanning/deliveryForm"
import { ReactLocalization } from "@fluent/react"
import { MAX_SEATS, MIN_SEATS, TEXT_FIELD_MAX_LENGTH } from "../../Static/MeetingPlanning/deliveryForm"

const isSupportedValidationType = createEnumChecker(ValidationType)
const isSupportedInputType = createEnumChecker(InputType)

export function isSupportedInputField(field: FormInputField, logger: Logger) {
    const result = isSupportedInputType(field.type) && field.validations.every((v) => isSupportedValidationType(v.type))

    if (!result) {
        if (!isSupportedInputType(field.type)) {
            logger.warn(`Unsupported input field type '${field.type}' for field:`, field)
        }
        field.validations.forEach((v) => {
            if (!isSupportedValidationType(v.type)) {
                logger.warn(`Unsupported validation type '${v.type}' for field:`, field)
            }
        })
    }

    return result
}

function getInitialValue(field: FormInputField) {
    if (field.initialValue !== undefined) return field.initialValue

    const fieldType = field.valueType ?? "string"

    switch (fieldType) {
        case "string":
            return ""
        case "number":
            return 0
        case "boolean":
            return false
        case "array":
            return []
        default:
            throw new UnreachableCaseError(fieldType)
    }
}

export function getInitialValues(fields: FormInputField[]) {
    return fields.reduce((acc, field) => {
        acc[field.name] = getInitialValue(field)
        return acc
    }, {} as FormikValues)
}

// Dynamically generate yup validation rules from a form input specification
// See: https://dev.to/franklin030601/dynamic-forms-with-formik-and-react-js-3no1

type YupString = Yup.StringSchema<string | undefined, AnyObject, string | undefined>
type YupNumber = Yup.NumberSchema<number | undefined, AnyObject, number | undefined>
type YupBoolean = Yup.BooleanSchema<boolean | undefined, AnyObject, boolean | undefined>

const getType = (key: string, type: string | undefined, inputFieldType: InputFieldType, isMultiline: boolean) => {
    if (inputFieldType === InputFieldType.TEXT_FIELD && type === 'ACCOUNTING_DIMENSION') {
        return InputType.ACCOUNTING_DIMENSION_TEXT_FIELD
    }

    if (inputFieldType === InputFieldType.TEXT_FIELD && isMultiline) {
        return InputType.TEXTAREA
    }

    if (key === 'DELIVERY_TYPE') {
        return InputType.DELIVERY_SELECT
    }

    if (key === 'OTHER_DELIVERY_LOCATION') {
        return InputType.OTHER_DELIVERY_COMPONENT
    }

    switch (inputFieldType) {
        case InputFieldType.AUTOCOMPLETE:
            return InputType.DYNAMIC_AUTOCOMPLETE
        case InputFieldType.NUMBER:
            return InputType.NUMBER_SELECT
        case InputFieldType.SELECT:
            return InputType.SELECT
        case InputFieldType.BUTTON:
            return InputType.BUTTON
        default:
            return InputType.TEXT
    }
}

const getValueType = (type: string | undefined, inputFieldType: InputFieldType) => {
    if (
        inputFieldType === InputFieldType.AUTOCOMPLETE ||
        type === 'ACCOUNTING_DIMENSION'
    ) {
        return 'array'
    }

    if (
        inputFieldType === InputFieldType.TEXT_FIELD ||
        inputFieldType === InputFieldType.SELECT
    ) {
        return 'string'
    }

    if (inputFieldType === InputFieldType.NUMBER) {
        return 'number'
    }

    return undefined
}

const getFieldValidation = (key: string, inputFieldType: InputFieldType, isRequired: boolean, validations: ValidationRule[], textFieldMaxLength: number, requiredMessage: string, maxLengthMessage: string, l10n: ReactLocalization): ValidationRule[] => {
    if (key === 'DELIVERY_LOCATIONS') {
        return [
            {
                type: ValidationType.DELIVERY_LOCATION_FIELDS_CONDITIONAL,
                when: {
                    field: 'DELIVERY_TYPE',
                    type: 'array',
                    is: DeliveryPayloadType.DELIVERY,
                    additionalFieldName: 'OTHER_DELIVERY_LOCATION'
                },
                message: l10n.getString(
                    'delivery-form-delivery-location-field-required',
                    undefined,
                    'Leveringssted er påkrævet',
                ),
            },
        ]
    }
    if (key === 'OTHER_DELIVERY_LOCATION') {
        return [
            {
                type: ValidationType.DELIVERY_LOCATION_FIELDS_CONDITIONAL,
                when: { field: 'DELIVERY_TYPE', type: 'string', is: DeliveryPayloadType.DELIVERY, additionalFieldName: 'DELIVERY_LOCATIONS' },
                message: l10n.getString(
                    'delivery-form-delivery-other-location-field-required',
                    undefined,
                    'Andet leveringssted er påkrævet',
                ),
            },
        ]
    }

    const maxLengthValidation = {
        type: ValidationType.MAX_LENGTH,
        value: textFieldMaxLength,
        message: maxLengthMessage
    } as ValidationRule

    const validationsList = validations.length ? [...validations, maxLengthValidation] : [maxLengthValidation]

    if (isRequired && inputFieldType === InputFieldType.AUTOCOMPLETE) {
        return [
            ...validationsList,
            {
                type: ValidationType.IS_EMPTY_LIST,
                message: requiredMessage
            } as ValidationRule
        ];
    }

    if (isRequired && inputFieldType === InputFieldType.NUMBER) {
        return [
            ...validationsList,
            {
                type: ValidationType.MIN,
                value: MIN_SEATS,
                message: requiredMessage
            } as ValidationRule,
            {
                type: ValidationType.MAX,
                value: MAX_SEATS,
                message: requiredMessage
            } as ValidationRule
        ];
    }

    if (isRequired) {
        return [
            ...validationsList,
            {
                type: ValidationType.REQUIRED,
                message: requiredMessage
            } as ValidationRule
        ];
    }

    return validationsList || [];
};

const mapMeetingFormInitialValues = (key: string, dimensionId: number | undefined, orderData: IBasketMeetingPlanningFormFieldOptional | undefined, fieldType: InputType, valueType: "string" | "number" | "boolean" | "array" | undefined) => {
    const getValueByFieldType = () => {
        if (fieldType === InputType.NUMBER_SELECT) {
            return ''
        }

        switch (valueType) {
            case 'array':
                return []
            case 'number':
                return 0
            default:
                return ''
        }
    }

    if (key.startsWith('ACCOUNTING_DIMENSION') && dimensionId) {
        const currentValue = orderData?.costCenters?.length ? orderData?.costCenters.find((item: IFormikSelectOptionsProps) => item.id === dimensionId) : null

        if (currentValue) {
            return orderData?.costCenters?.length ? orderData.costCenters : []
        }
    }

    switch (key) {
        case 'MEETING_NAME':
            return orderData?.meetingName
        case 'NUMBER_OF_SEATS':
            return orderData?.seats || ''
        case 'DELIVERY_TYPE':
            return orderData?.deliveryType
        case 'DELIVERY_LOCATIONS':
            return orderData?.deliveryType === DeliveryPayloadType.DELIVERY && orderData?.deliveryLocation ? [{ label: orderData.deliveryLocation, value: orderData.deliveryLocation }] : []
        case 'MERCHANT_ORDER_NOTE':
            return orderData?.otherMessage || ''
        case 'BUYER_PARTIES':
            return orderData?.billReceiver?.length ? orderData.billReceiver : []
        default:
           return getValueByFieldType()
    }
}

export const dynamicFieldToInputField = (field: IConfigField, initialMeetingPlanningData: IEditMeetingOrderData | null, requiredMessage: string, l10n: ReactLocalization): FormInputField => {
    const { key, type, inputField  } = field
    const { type: inputFieldType, isMultiline, isRequired, validations } = inputField
    const textFieldMaxLength = TEXT_FIELD_MAX_LENGTH

    const maxLengthMessage = l10n.getString('error-max-length', { maxLength: textFieldMaxLength }, `Maksimal længde er ${textFieldMaxLength}`)

    const fieldValidations = getFieldValidation(key, inputFieldType, isRequired || false, validations || [], textFieldMaxLength, requiredMessage, maxLengthMessage, l10n)
    const fieldType = getType(key, type, inputFieldType, isMultiline || false)
    const valueType = getValueType(type, inputFieldType)
    const initialValue = mapMeetingFormInitialValues(field.key, field.dimensionId, initialMeetingPlanningData?.orderData, fieldType, valueType)

    return {
        name: field.key,
        label: field.label,
        title: field.label,
        placeholder: field.label,
        valueType,
        initialValue,
        validations: fieldValidations,
        helperText: '',
        type: fieldType,
        key: field.key,
        isActive: field.active,
        searchQuery: field?.inputField?.searchQuery,
        options: field?.selectValues?.length ? field.selectValues : [],
        variant: field.inputField.type === InputFieldType.TEXT_FIELD ? 'outlined' : 'standard',
        dimensionId: field?.dimensionId || null,
        required: field.inputField.isRequired,
        action(): void {},
        onDeliveryChange(deliveryType: DeliveryPayloadType): void {},
    }
}

function generateFieldSchema(field: FormInputField) {
    let schema = Yup[field.valueType ? field.valueType : "string"]()

    for (const rule of field.validations) {
        switch (rule.type) {
            case ValidationType.IS_EMPTY_LIST:
                schema = Yup.array()
                    .of(Yup.object({
                        label: Yup.string().required(),
                        value: Yup.string().required()
                    }))
                    .test('is-not-empty', rule.message, (value) => Array.isArray(value) && value.length > 0);
                break;
            case ValidationType.REQUIRED:
                schema = schema.required(rule.message)
                break
            case ValidationType.IS_TRUE:
                schema = (schema as YupBoolean).isTrue(rule.message)
                break
            case ValidationType.EMAIL:
                schema = (schema as YupString).email(rule.message)
                break
            case ValidationType.REGEX: {
                const raw = rule.regex
                const clean = raw.replace(/^["']|["']$/g, '')

                if (field.valueType === 'string') {
                    return schema = (schema as YupString).matches(new RegExp(clean), rule.message)
                }

                return schema = Yup.array().test('regex-validation', rule.message, (value) => {
                    if (!value || !Array.isArray(value)) return true
                    return value.every(item =>
                        typeof item === 'object' && item.value && new RegExp(clean).test(item.value)
                    )
                })
            }
            case ValidationType.MAX_LENGTH:
                schema = (schema as YupString).max(rule.value, rule.message)
                break
            case ValidationType.MIN:
                schema = (schema as YupNumber).min(rule.value, rule.message)
                break
            case ValidationType.MAX:
                schema = (schema as YupNumber).max(rule.value, rule.message)
                break
            case ValidationType.DELIVERY_LOCATION_FIELDS_CONDITIONAL:
                if (rule.when.type === 'array') {
                    return schema = Yup.array()
                        .of(
                            Yup.object({
                                label: Yup.string().required(),
                                value: Yup.string().required(),
                            })
                        )
                        .test(ValidationType.DELIVERY_LOCATION_FIELDS_CONDITIONAL, rule.message, function (value) {
                            const { parent } = this;
                            const isPrimaryMatch = parent[rule.when.field] === rule.when.is;
                            const isAdditionalFieldValue = rule?.when?.additionalFieldName && parent[rule.when.additionalFieldName]

                            if (!isPrimaryMatch) return true

                            if (isAdditionalFieldValue && Array.isArray(value) && !value.length) return true

                            return Array.isArray(value) && value.length > 0
                        });
                }

                schema = (schema as YupString)
                    .test(ValidationType.DELIVERY_LOCATION_FIELDS_CONDITIONAL, rule.message, function (value) {
                        const { parent } = this;
                        const isPrimaryMatch = parent[rule.when.field] === rule.when.is;
                        const isAdditionalFieldValue = rule?.when?.additionalFieldName && parent[rule.when.additionalFieldName]?.length

                        if (!isPrimaryMatch || isAdditionalFieldValue) return true

                        return  typeof value === 'string' && value.trim().length > 0;
                    });
                break
            default:
                throw new UnreachableCaseError(rule)
        }
    }

    return schema
}

export function generateFormSchema(fields: FormInputField[]) {
    const fieldSchemas = fields.reduce((acc, field) => {
        acc[field.name] = generateFieldSchema(field)
        return acc
    }, {} as Record<string, ReturnType<typeof generateFieldSchema>>)

    return Yup.object({ ...fieldSchemas })
}
